/**
 * Niche-landing config.
 *
 * Each entry powers one programmatic landing page at `/for/<slug>` targeted
 * at a specific spiritual practitioner segment (Reiki Masters, Babalawos,
 * Tarot Readers, etc.).
 *
 * Why programmatic, not 17 hand-written components:
 *   - SEO: each landing page is its own indexable URL with niche-specific
 *     copy, headline and a hero image — that's the part that ranks. The
 *     layout is identical so duplicating the component is pure cost.
 *   - Performance: 17 components → 17 routes that all bundle the same
 *     React tree. One component + a data table → tree-shake-friendly,
 *     small bundle delta.
 *   - Editing: tweaking a single Section 3 bullet on all 17 pages is
 *     one edit here, not 17 edits across the repo.
 *
 * Sections (per user spec):
 *   Hero          – large image + headline + "Start Free" CTA
 *   Section 2     – Pain Points (the 5 reasons their current life sucks)
 *   Section 3     – How Cauldron Helps (7 product proof points)
 *   Section 4     – Screenshots (App / Reviews / Payments / Messaging / Profile)
 *   Section 5     – Testimonials (early-user stories, optional)
 *   Section 6     – CTA (Join Free / Become Verified)
 *
 * Image sourcing:
 *   We avoid generic stock images — instead use thematic Unsplash photos
 *   that visually evoke the practice (sacred geometry, ritual objects,
 *   tarot spreads, fire, candles, etc). Each hero image is hand-picked
 *   per niche, not algorithmically chosen.
 */

export const NICHES = [
  {
    slug: "reiki-masters",
    label: "Reiki Masters",
    pluralName: "Reiki Masters",
    headline: "Grow Your Reiki Practice With More Verified Clients",
    subhead:
      "Stop chasing referrals on Facebook. Cauldron sends you energy-aware seekers actively looking for distance Reiki and in-person sessions — and pays you the same day.",
    heroImage:
      "https://images.unsplash.com/photo-1601925240970-98447ad17472?auto=format&fit=crop&w=1600&q=70",
    practiceTerm: "Reiki",
    keywords:
      "Reiki Master, Reiki practitioner platform, online Reiki, distance Reiki booking, Reiki marketplace, find Reiki clients online, verified Reiki, Reiki business growth",
  },
  {
    slug: "tarot-readers",
    label: "Tarot Readers",
    pluralName: "Tarot Readers",
    headline: "Read Tarot for Real Clients — Not Algorithms",
    subhead:
      "Cauldron is where seekers come specifically for trusted tarot guidance. Build a verified profile, post your own spreads, and book clients without the social-media grind.",
    heroImage:
      "https://images.unsplash.com/photo-1659540098310-1d4cd61c1c3d?auto=format&fit=crop&w=1600&q=70",
    practiceTerm: "tarot",
    keywords:
      "tarot reader platform, online tarot reading, professional tarot, tarot business, find tarot clients, tarot booking app",
  },
  {
    slug: "psychic-mediums",
    label: "Psychic Mediums",
    pluralName: "Psychic Mediums",
    headline: "A Sacred Space for Verified Psychic Mediums",
    subhead:
      "Stop competing with $5 psychics on giant marketplaces. Cauldron's verification keeps the platform serious — and the rates protected.",
    heroImage:
      "https://images.unsplash.com/photo-1518709268805-4e9042af9f23?auto=format&fit=crop&w=1600&q=70",
    practiceTerm: "psychic mediumship",
    keywords:
      "psychic medium platform, professional medium, ancestor communication, online medium reading, verified psychic, spirit connection",
  },
  {
    slug: "astrologers",
    label: "Astrologers",
    pluralName: "Astrologers",
    headline: "Where Verified Astrologers Build Real Clienteles",
    subhead:
      "Birth charts, transits, synastry — Cauldron seekers come knowing what they want and pay for the depth, not 2-minute snapshots.",
    heroImage:
      "https://images.unsplash.com/photo-1532968961962-8a0cb3a2d4f5?auto=format&fit=crop&w=1600&q=70",
    practiceTerm: "astrology",
    keywords:
      "astrologer platform, online astrology, birth chart reading, transit reading, professional astrologer, astrology business",
  },
  {
    slug: "hoodoo-practitioners",
    label: "Hoodoo Practitioners",
    pluralName: "Hoodoo Practitioners",
    headline: "A Respectful Home for Hoodoo Workers",
    subhead:
      "Cauldron honors the lineage. We verify your practice, protect your craft, and connect you to clients who already know the work — no explaining required.",
    heroImage:
      "https://images.unsplash.com/photo-1604147495798-57beb5d6af73?auto=format&fit=crop&w=1600&q=70",
    practiceTerm: "Hoodoo",
    keywords:
      "Hoodoo practitioner, conjure work, rootwork platform, Black spiritual tradition, Hoodoo readings online, ancestral spiritual work",
  },
  {
    slug: "conjure-workers",
    label: "Conjure Workers",
    pluralName: "Conjure Workers",
    headline: "Conjure Workers — Get Paid What Your Craft Is Worth",
    subhead:
      "Set your own rates, set your own boundaries. Cauldron clients come to Conjure workers because they trust the practice — not because you're the cheapest option.",
    heroImage:
      "https://images.unsplash.com/photo-1551582045-6ec9c11d8697?auto=format&fit=crop&w=1600&q=70",
    practiceTerm: "Conjure",
    keywords:
      "Conjure worker, candle work, oil work, Conjure marketplace, professional Conjure, spiritual work clients",
  },
  {
    slug: "rootworkers",
    label: "Rootworkers",
    pluralName: "Rootworkers",
    headline: "Rootworkers — Built for the Way You Actually Work",
    subhead:
      "Long-form consultations. Multi-session work. Custom mojo and fixed candles. Cauldron's booking, payments and messaging are built for spiritual work that lasts more than one Zoom call.",
    heroImage:
      "https://images.unsplash.com/photo-1556057194-eea9b27a0bbc?auto=format&fit=crop&w=1600&q=70",
    practiceTerm: "rootwork",
    keywords:
      "rootworker, professional rootwork, conjure rootwork, Southern spiritual tradition, rootwork bookings, custom spiritual work",
  },
  {
    slug: "ifa-priests",
    label: "Ifá Priests",
    pluralName: "Ifá Priests",
    headline: "Ifá Priests — Reach the Òrìṣà Community Worldwide",
    subhead:
      "From Lagos to Havana to NYC, Ifá seekers are looking for verified Babalawos and Iyanifás. Cauldron's lineage verification puts your initiation front and center.",
    heroImage:
      "https://images.unsplash.com/photo-1614080362787-1ba6bcd2e9f5?auto=format&fit=crop&w=1600&q=70",
    practiceTerm: "Ifá",
    keywords:
      "Ifá priest, Babalawo platform, Iyanifa, Òrìṣà consultation, Yoruba spirituality, Isese tradition, online Ifá reading",
  },
  {
    slug: "babalawos",
    label: "Babalawos",
    pluralName: "Babalawos",
    headline: "Babalawos — The Verified Ifá Marketplace",
    subhead:
      "Built with respect for the òdú, the lineage, and the work. Cauldron is the only platform where your initiation is verified before you're listed.",
    heroImage:
      "https://images.unsplash.com/photo-1612799929193-0f02e0f24c64?auto=format&fit=crop&w=1600&q=70",
    practiceTerm: "Ifá",
    keywords:
      "Babalawo near me, Babalawo platform, online Ifá consultation, verified Babalawo, Yoruba Ifá priest, Orunmila",
  },
  {
    slug: "lucumi-priests",
    label: "Lucumí Priests",
    pluralName: "Lucumí Priests",
    headline: "Lucumí Priests — A Verified Home for the Religion",
    subhead:
      "Cauldron honors the Regla de Ocha. We verify your padrino/madrina lineage, your asiento year, and protect the work from the imitations that crowd other platforms.",
    heroImage:
      "https://images.unsplash.com/photo-1604147495798-57beb5d6af73?auto=format&fit=crop&w=1600&q=70",
    practiceTerm: "Lucumí",
    keywords:
      "Lucumí priest, Santería priest, Regla de Ocha, Cuban Òrìṣà tradition, verified Santero, Lucumí consultation",
  },
  {
    slug: "palo-mayombe-practitioners",
    label: "Palo Mayombe Practitioners",
    pluralName: "Palo Mayombe Practitioners",
    headline: "Palo Mayombe — A Platform That Respects the Nganga",
    subhead:
      "Tatas and Yayas, this is your space. Cauldron verifies your rama, protects the work, and connects you to seekers who already know what Palo is — no education required.",
    heroImage:
      "https://images.unsplash.com/photo-1518709268805-4e9042af9f23?auto=format&fit=crop&w=1600&q=70",
    practiceTerm: "Palo Mayombe",
    keywords:
      "Palo Mayombe practitioner, Tata Nganga, Palo consultation, Congo spiritual tradition, verified Palo, nganga work",
  },
  {
    slug: "vodou-priests",
    label: "Vodou Priests",
    pluralName: "Vodou Priests",
    headline: "Houngans & Mambos — Serve the Lwa, Serve Real Clients",
    subhead:
      "Cauldron honors Haitian Vodou. We verify your initiation, your asson, and your sosyete — and connect you to a global Vodou community looking for serious spiritual guidance.",
    heroImage:
      "https://images.unsplash.com/photo-1604147495798-57beb5d6af73?auto=format&fit=crop&w=1600&q=70",
    practiceTerm: "Vodou",
    keywords:
      "Vodou priest, Houngan, Mambo, Haitian Vodou, Lwa, Vodou consultation, verified Vodou priest",
  },
  {
    slug: "quimbanda-practitioners",
    label: "Quimbanda Practitioners",
    pluralName: "Quimbanda Practitioners",
    headline: "Quimbanda — A Verified Marketplace for the Tradition",
    subhead:
      "Exus, Pomba Giras, the Reino — Cauldron's verification process protects authentic Quimbanda practitioners from the wave of imitators flooding mainstream platforms.",
    heroImage:
      "https://images.unsplash.com/photo-1542665952-14513db15293?auto=format&fit=crop&w=1600&q=70",
    practiceTerm: "Quimbanda",
    keywords:
      "Quimbanda practitioner, Exu consultation, Pomba Gira, Brazilian spiritual tradition, verified Quimbanda, Reino de Quimbanda",
  },
  {
    slug: "spiritual-coaches",
    label: "Spiritual Coaches",
    pluralName: "Spiritual Coaches",
    headline: "Spiritual Coaches — Build a 6-Figure Practice Without Burnout",
    subhead:
      "Cauldron handles bookings, payments, reviews and communities so you can spend your time actually coaching — not chasing leads on Instagram.",
    heroImage:
      "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=1600&q=70",
    practiceTerm: "spiritual coaching",
    keywords:
      "spiritual coach platform, online spiritual coaching, spiritual mentor, soul coach, manifestation coach, spiritual business growth",
  },
  {
    slug: "energy-healers",
    label: "Energy Healers",
    pluralName: "Energy Healers",
    headline: "Energy Healers — Get Found by Clients Who Believe",
    subhead:
      "No more explaining what you do at parties. Cauldron seekers already understand energy work — they're here to book it, not to be sold it.",
    heroImage:
      "https://images.unsplash.com/photo-1593811167562-9cef47bfc4d7?auto=format&fit=crop&w=1600&q=70",
    practiceTerm: "energy healing",
    keywords:
      "energy healer, energy healing online, distance healing, chakra healing, aura cleansing, professional energy worker",
  },
  {
    slug: "crystal-healers",
    label: "Crystal Healers",
    pluralName: "Crystal Healers",
    headline: "Crystal Healers — A Beautiful, Brand-Worthy Practice Page",
    subhead:
      "Stop posting beautiful flatlays on IG and getting zero clients. Cauldron lets you showcase your crystal grids, sessions and shop in one verified profile that converts.",
    heroImage:
      "https://images.unsplash.com/photo-1611068120813-eca5a8cbf793?auto=format&fit=crop&w=1600&q=70",
    practiceTerm: "crystal healing",
    keywords:
      "crystal healer, crystal therapy, crystal grid, healing crystals, professional crystal healer, online crystal consultation",
  },
  {
    slug: "hypnotherapists",
    label: "Hypnotherapists",
    pluralName: "Hypnotherapists",
    headline: "Hypnotherapists — The Spiritual Side of Your Practice, Finally Found",
    subhead:
      "Past-life regression. Soul-level work. Spirit release. Cauldron is the platform where clients searching for transformational hypnotherapy actually find you.",
    heroImage:
      "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?auto=format&fit=crop&w=1600&q=70",
    practiceTerm: "hypnotherapy",
    keywords:
      "hypnotherapist platform, past life regression, spiritual hypnotherapy, soul regression, online hypnotherapy, transformational hypnotherapy",
  },
];

export const NICHE_BY_SLUG = Object.fromEntries(NICHES.map((n) => [n.slug, n]));
